import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Home as HomeIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Description as BillIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  AccountBalance as DepositIcon,
  Build as MaintenanceIcon,
  SwapHoriz as TransferIcon,
  WaterDrop as WaterIcon,
} from '@mui/icons-material';
import authService from '../../services/authService';

const drawerWidth = 240;

// SuperAdmin menu - platform management only
const superAdminMenuItems = [
  { text: 'Platform Overview', icon: <DashboardIcon />, path: '/admin/dashboard' },
  { text: 'Manage Landlords', icon: <PeopleIcon />, path: '/admin/landlords' },
  { text: 'Wallet', icon: <PaymentIcon />, path: '/admin/wallet' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
];

// Admin menu (landlord) - full access including messaging & settings
const adminMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Tenants', icon: <PeopleIcon />, path: '/tenants' },
  { text: 'Rooms', icon: <HomeIcon />, path: '/rooms' },
  { text: 'Invoices', icon: <ReceiptIcon />, path: '/invoices' },
  { text: 'Payments', icon: <PaymentIcon />, path: '/payments' },
  { text: 'Bills', icon: <BillIcon />, path: '/bills' },
  { text: 'Water Readings', icon: <WaterIcon />, path: '/water-readings' },
  { text: 'Deposits', icon: <DepositIcon />, path: '/deposits' },
  { text: 'Maintenance', icon: <MaintenanceIcon />, path: '/maintenance' },
  { text: 'Transfers', icon: <TransferIcon />, path: '/transfers' },
  { text: 'Reports', icon: <AssessmentIcon />, path: '/reports' },
  { text: 'Messaging', icon: <MenuIcon />, path: '/messaging' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
];

// Caretaker menu - view access, no messaging or system settings
const caretakerMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Tenants', icon: <PeopleIcon />, path: '/tenants' },
  { text: 'Rooms', icon: <HomeIcon />, path: '/rooms' },
  { text: 'Invoices', icon: <ReceiptIcon />, path: '/invoices' },
  { text: 'Payments', icon: <PaymentIcon />, path: '/payments' },
  { text: 'Bills', icon: <BillIcon />, path: '/bills' },
  { text: 'Water Readings', icon: <WaterIcon />, path: '/water-readings' },
  { text: 'Deposits', icon: <DepositIcon />, path: '/deposits' },
  { text: 'Maintenance', icon: <MaintenanceIcon />, path: '/maintenance' },
  { text: 'Transfers', icon: <TransferIcon />, path: '/transfers' },
  { text: 'Reports', icon: <AssessmentIcon />, path: '/reports' },
  { text: 'Profile', icon: <SettingsIcon />, path: '/settings' },
];

const tenantMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/tenant/dashboard' },
  { text: 'My Invoices', icon: <ReceiptIcon />, path: '/tenant/invoices' },
  { text: 'Payments', icon: <PaymentIcon />, path: '/tenant/payments' },
  { text: 'My Bills', icon: <BillIcon />, path: '/tenant/bills' },
  { text: 'Water Usage', icon: <WaterIcon />, path: '/tenant/water-readings' },
  { text: 'Deposits', icon: <DepositIcon />, path: '/tenant/deposits' },
  { text: 'Maintenance', icon: <MaintenanceIcon />, path: '/tenant/maintenance' },
  { text: 'Transfers', icon: <TransferIcon />, path: '/tenant/transfers' },
  { text: 'Profile', icon: <SettingsIcon />, path: '/settings' },
];

const MainLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const currentUser = authService.getCurrentUser();
  const getMenuItems = () => {
    if (currentUser?.role === 'superuser') return superAdminMenuItems;
    if (currentUser?.role === 'tenant') return tenantMenuItems;
    if (currentUser?.role === 'caretaker') return caretakerMenuItems;
    return adminMenuItems; // landlord
  };
  const menuItems = getMenuItems();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
        }}
      >
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
          CISTenant
        </Typography>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: location.pathname === item.path ? 'primary.main' : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Property Management System
          </Typography>
          <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {currentUser?.username?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem disabled>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {currentUser?.username || 'User'}
              </Typography>
            </MenuItem>
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                {currentUser?.email || 'user@example.com'}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { handleMenuClose(); handleNavigation('/settings'); }}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;
